import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createVNode as _createVNode, mergeProps as _mergeProps, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import config from '@/config'
import { delay } from '@/components/Tippy'
import { session } from '@/composables/useSession'
import { useActivitiesCount } from '@/composables/useActivitiesCount'
import useAssignmentSection from '@/modules/tasks/composables/useAssignmentSection'
import analytics from '@/utils/analytics'
import { hasWorkspaceRole } from '@/utils/roleUtils'
import { bus } from '@/utils/bus'
import useBreakpoints from '@/composables/useBreakpoints'
import { useDashboardsList } from '@/composables/useDashboardsList'
import useNavbarRoutes from '@/composables/useNavbarRoutes'
import useTicketRouteParams from '@/composables/useTicketRouteParams'

import Goal from './UserGoal.vue'
import NavLogo from './NavLogo/NavLogo.vue'
import NavbarCoachingIcon from './NavbarCoachingIcon.vue'
import NavbarLink from './NavbarLink.vue'

const HOVER_TIMER = 400

export default /*@__PURE__*/_defineComponent({
  __name: 'Navbar',
  setup(__props) {

const NavConstructionLogo = defineAsyncComponent(() => import('./NavLogo/NavConstructionLogo.vue'))

const activitiesCount = useActivitiesCount()
const { dashboardList, lookerReviewsDashboard, lookerReviewsAgentDashboard } = useDashboardsList()
const { personalAssignments } = useAssignmentSection()
const { assignmentId, calibrationSessionId } = useTicketRouteParams()

const disputesCount = ref(0)

const { navbarRoutesMap } = useNavbarRoutes()
const { breakpoints } = useBreakpoints()
const floatingPageNav = breakpoints.smaller('lg')
// Starts a delay timer to trigger a floating page nav if user does not interact with anything within 400ms
let hoverTimeout: ReturnType<typeof setTimeout> | undefined = undefined

const selfReviews = computed(() => session.workspace?.settings?.selfReviews || false)
const activitesCountTotal = computed(() => activitiesCount.value?.total || 0)

const route = useRoute()
const router = useRouter()

const dashboardRoute = computed(() => {
  if (dashboardList.value.length && (lookerReviewsDashboard.value || lookerReviewsAgentDashboard.value)) {
    return hasWorkspaceRole('AGENT') ? lookerReviewsAgentDashboard.value : lookerReviewsDashboard.value
  }

  return ''
})

const onConversationsClick = () => {
  analytics.navbarEvent('Clicked navbar', 'Conversations')
  const { singleTicket, ...query } = route.query
  if (singleTicket) router.replace({ query })
}

const startHoverCounter = () => {
  if (!floatingPageNav.value) return
  if (hoverTimeout) clearTimeout(hoverTimeout)
  hoverTimeout = setTimeout(() => {
    hoverTimeout = undefined
    bus.$emit('show-page-nav')
  }, HOVER_TIMER)
}

const stopHoverCounter = () => {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout)
    hoverTimeout = undefined
  }
}

const handleInteraction = (event: MouseEvent) => {
  if (event.target instanceof HTMLElement && event.target.closest('.navbar__link')) {
    stopHoverCounter()
  }
}

return (_ctx: any,_cache: any) => {
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createElementBlock("nav", _mergeProps(_ctx.$attrs, {
    class: "navbar",
    "data-testId": "navbar",
    onMouseenter: startHoverCounter,
    onMouseleave: stopHoverCounter,
    onClick: handleInteraction
  }), [
    (_unref(config).flagManager)
      ? (_openBlock(), _createBlock(_unref(NavConstructionLogo), { key: 0 }))
      : (_openBlock(), _createBlock(NavLogo, { key: 1 })),
    (dashboardRoute.value && 'id' in dashboardRoute.value)
      ? (_openBlock(), _createBlock(NavbarLink, {
          key: 2,
          title: _ctx.$t(_unref(navbarRoutesMap).dashboard.label),
          to: { name: 'dashboard.looker', params: { id: dashboardRoute.value.id } },
          "data-testid": "navbar-dashboards",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(analytics).navbarEvent('Clicked navbar', 'Dashboard')))
        }, {
          default: _withCtx(() => [
            (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(navbarRoutesMap).dashboard.icon)))
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["title", "to"]))
      : _createCommentVNode("v-if", true),
    (!_unref(hasWorkspaceRole)('AGENT') || selfReviews.value)
      ? (_openBlock(), _createBlock(NavbarLink, {
          key: 3,
          title: _ctx.$t(_unref(navbarRoutesMap).conversations.label),
          "data-testid": "navbar-conversations",
          to: { name: 'conversations' },
          class: _normalizeClass([disputesCount.value && _ctx.$style.notificationBubble]),
          "data-highlight-count": (disputesCount.value > 99 ? '99+' : disputesCount.value) || null,
          "hide-tooltip": "",
          onClick: onConversationsClick
        }, {
          default: _withCtx(({ active }) => [
            active
              ? (_openBlock(), _createBlock(Goal, { key: 0 }))
              : _withDirectives((_openBlock(), _createBlock(_resolveDynamicComponent(_unref(navbarRoutesMap).conversations.icon), { key: 1 }, null, 512 /* NEED_PATCH */)), [
                  [_directive_tippy, {
          placement: 'right',
          content: _ctx.$t('modules.application.navbar.conversations'),
          delay: _unref(delay),
          offset: [0, 10],
        }]
                ])
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["title", "class", "data-highlight-count"]))
      : _createCommentVNode("v-if", true),
    (!_unref(hasWorkspaceRole)('AGENT'))
      ? (_openBlock(), _createBlock(NavbarLink, {
          key: 4,
          title: _ctx.$t(_unref(navbarRoutesMap).tasks.label),
          "data-testid": "navbar-tasks",
          to: {
        name: 'tasks',
        params: _unref(assignmentId) ? { assignmentId: _unref(assignmentId) } : { calibrationSessionId: _unref(calibrationSessionId) },
      },
          class: _normalizeClass([_unref(personalAssignments)?.length && _ctx.$style.notificationBubble]),
          "data-highlight-count": (_unref(personalAssignments)?.length > 99 ? '99+' : _unref(personalAssignments)?.length) || null,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(analytics).navbarEvent('Clicked navbar', 'Tasks')))
        }, {
          default: _withCtx(() => [
            (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(navbarRoutesMap).tasks.icon)))
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["title", "to", "class", "data-highlight-count"]))
      : _createCommentVNode("v-if", true),
    _createVNode(NavbarLink, {
      title: _ctx.$t(_unref(navbarRoutesMap).activity.label),
      to: { name: 'activity' },
      class: _normalizeClass([activitesCountTotal.value > 0 && _ctx.$style.notificationBubble]),
      "data-highlight-count": (activitesCountTotal.value > 99 ? '99+' : activitesCountTotal.value) || null,
      onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(analytics).navbarEvent('Clicked navbar', 'Activity')))
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(navbarRoutesMap).activity.icon)))
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["title", "class", "data-highlight-count"]),
    _createVNode(NavbarCoachingIcon, { class: "navbar__link" })
  ], 16 /* FULL_PROPS */))
}
}

})